import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Loader2, Check, Camera, Receipt, ShieldCheck, AlertTriangle,
  Ban, CornerUpLeft, Stethoscope, Play, Wrench, Send, BadgeCheck, Banknote, Gauge,
} from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

/**
 * One maintenance job, walked one step at a time.
 *
 * The backend has always modelled a repair as eight stages with real controls
 * between them: a diagnosis before anyone touches the truck, before-and-after
 * photographs before the work counts as done, an invoice before it is sent to
 * be checked, a checker who is neither the reporter nor the mechanic, and an
 * approver who is not the checker. None of that was visible on screen, so the
 * one button that skipped all of it — settle — is the one everybody pressed.
 * Ninety-six of the first hundred and two jobs went straight to paid with no
 * diagnosis and no odometer reading behind them.
 *
 * So this drawer shows the whole ladder: what has happened, who did it, what
 * happens next, and exactly what is still missing before the next step will be
 * allowed. A gate the operator can see is a gate they can clear; a gate they
 * cannot see is only a 412 at the worst moment.
 */

interface Detail {
  event: Record<string, unknown>;
  photos: Array<{ id: string; photo_kind: string }>;
  partsUsed: Array<{ id: string }>;
  invoices: Array<{ id: string; code: string; invoice_number: string | null }>;
  audit: Array<{ id: string; action: string; performed_at: string; performed_by_name?: string | null }>;
}

/** A free-text field the step collects on its way out. */
interface StepField {
  name: string;
  label: string;
  placeholder: string;
  required: boolean;
}

interface Step {
  status: string;
  label: string;
  icon: typeof Wrench;
  /** Column on the event that records when this step happened. */
  at?: string;
  /** Column carrying the name of whoever did it. */
  who?: string;
  /** The move that leaves this step. Absent on the last one. */
  action?: {
    path: string;
    cta: string;
    field?: StepField;
    /** Said before the button, so the operator knows what they are signing. */
    hint: string;
  };
}

const STEPS: Step[] = [
  {
    status: 'reported',
    label: 'Reported',
    icon: AlertTriangle,
    at: 'reported_at',
    who: 'reporter_name',
    action: {
      path: 'diagnose',
      cta: 'Record the diagnosis',
      hint: 'What is actually wrong — not what was reported. This is the line that separates a repair from a guess, and nothing can start without it.',
      field: {
        name: 'diagnosis',
        label: 'Diagnosis',
        placeholder: 'e.g. Front brake pads worn past the wear indicator; near-side disc scored and needs skimming.',
        required: true,
      },
    },
  },
  {
    status: 'diagnosed',
    label: 'Diagnosed',
    icon: Stethoscope,
    at: 'diagnosed_at',
    action: {
      path: 'start-work',
      cta: 'Start the work',
      hint: 'Starts the clock and records you as the mechanic. Take the BEFORE photograph now — you will not be able to close the job without it.',
    },
  },
  {
    status: 'in_progress',
    label: 'Work started',
    icon: Play,
    at: 'work_started_at',
    who: 'performed_by_name',
    action: {
      path: 'complete-work',
      cta: 'Mark the work done',
      hint: 'A before and an after photograph are required. Only an urgent breakdown may skip them.',
    },
  },
  {
    status: 'work_complete',
    label: 'Work done',
    icon: Wrench,
    at: 'work_completed_at',
    action: {
      path: 'submit-for-verification',
      cta: 'Send for checking',
      hint: 'Hands the job to someone else to confirm. An invoice must be attached first, unless the job is explicitly marked as having none.',
    },
  },
  {
    status: 'pending_verification',
    label: 'Waiting to be checked',
    icon: Send,
    action: {
      path: 'verify',
      cta: 'Confirm the work',
      hint: 'The checker cannot be the person who reported the fault or the one who repaired it. That rule is enforced by the server, not by this screen.',
      field: {
        name: 'verificationNotes',
        label: 'Notes (optional)',
        placeholder: 'What you inspected, and anything the mechanic should know.',
        required: false,
      },
    },
  },
  {
    status: 'verified',
    label: 'Checked',
    icon: ShieldCheck,
    at: 'verified_at',
    who: 'verifier_name',
    action: {
      path: 'approve',
      cta: 'Approve for payment',
      hint: 'Releases the cost for payment. The approver must be someone other than the person who checked it.',
      field: {
        name: 'approvalNotes',
        label: 'Notes (optional)',
        placeholder: 'Budget line, or why this was approved above the usual rate.',
        required: false,
      },
    },
  },
  {
    status: 'approved_for_payment',
    label: 'Approved',
    icon: BadgeCheck,
    at: 'approved_at',
    who: 'approver_name',
    action: {
      path: 'mark-paid',
      cta: 'Mark it paid',
      hint: 'Only paid cost reaches the monthly total and the SAR-per-kilometre ranking.',
      field: {
        name: 'paymentReference',
        label: 'Payment reference (optional)',
        placeholder: 'Transfer number, cheque number, or petty-cash slip.',
        required: false,
      },
    },
  },
  {
    status: 'paid',
    label: 'Paid',
    icon: Banknote,
    at: 'paid_at',
  },
];

const sar = (v: unknown) =>
  v == null || v === '' ? '—' : `${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SAR`;
const day = (v: unknown) => (v ? String(v).slice(0, 10) : null);

/**
 * Tyre work is recorded, not inspected — the same list the server enforces.
 * A fitted tyre is either on the truck or it is not, and the fitter is standing
 * next to it; a checker and an approver a day later settle nothing. What the
 * job has to leave behind is the reading it was fitted at and a picture proving
 * it was, because those two are what turn tyres into a cost per kilometre.
 */
const TYRE_TYPES = ['tire_replacement', 'tire_rotation', 'tire_puncture', 'tire_wear'];

export function JobStepper({
  eventId, typeLabel, onClose, onChanged,
}: {
  eventId: string;
  typeLabel: (t: string) => string;
  onClose: () => void;
  /** Fired after any successful move so the page behind can refresh its counts. */
  onChanged?: () => void;
}) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [draft, setDraft] = useState('');
  const [closing, setClosing] = useState<null | 'cancel' | 'reject'>(null);
  const [closeReason, setCloseReason] = useState('');
  // Tyre path only.
  const [odo, setOdo] = useState('');
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr, setPhotoErr] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['maintenance-event', eventId],
    queryFn: async () => (await apiClient.get<Detail>(`/api/v1/maintenance/events/${eventId}`)).data,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['maintenance-event', eventId] });
    onChanged?.();
  };

  const move = useMutation({
    mutationFn: async ({ path, body }: { path: string; body?: Record<string, unknown> }) =>
      apiClient.post(`/api/v1/maintenance/events/${eventId}/${path}`, body ?? {}),
    onSuccess: () => { setDraft(''); setCloseReason(''); setClosing(null); refresh(); },
  });

  const e = data?.event ?? {};
  const status = String(e.status ?? '');
  const priority = String(e.priority ?? '');
  const isTyre = TYRE_TYPES.includes(String(e.event_type ?? ''));

  // The reading already on the job is what the box should open with, so an
  // edit is a correction rather than a re-entry.
  useEffect(() => {
    const v = data?.event?.odometer_at_service;
    if (v !== null && v !== undefined) setOdo(String(v));
  }, [data?.event?.odometer_at_service]);

  /** Tyre close: save the reading if it changed, then settle in one move. */
  const closeTyre = useMutation({
    mutationFn: async () => {
      const n = Number(odo);
      if (Number.isFinite(n) && n > 0 && String(n) !== String(e.odometer_at_service ?? '')) {
        await apiClient.patch(`/api/v1/maintenance/events/${eventId}`, { odometerAtService: n });
      }
      await apiClient.post(`/api/v1/maintenance/events/${eventId}/settle`, { paid: true });
    },
    onSuccess: refresh,
  });

  async function uploadTyrePhoto(file: File) {
    setPhotoBusy(true);
    setPhotoErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      // The tyre that is now on the truck. old_part and odometer are still
      // available from the full record for anyone who wants to add them.
      fd.append('kind', 'new_part');
      await apiClient.post(`/api/v1/maintenance/events/${eventId}/photos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      refresh();
    } catch (err) {
      setPhotoErr(extractErrorMessage(err));
    } finally {
      setPhotoBusy(false);
    }
  }
  const currentIndex = STEPS.findIndex((s) => s.status === status);
  const step = currentIndex >= 0 ? STEPS[currentIndex] : null;

  // The gates, computed here so they can be shown as a checklist rather than
  // discovered as a rejection.
  const photoCount = data?.photos.length ?? 0;
  const odoOk = Number(odo) > 0;
  const alreadyClosed = status === 'paid';

  const before = data?.photos.filter((p) => p.photo_kind === 'before').length ?? 0;
  const after = data?.photos.filter((p) => p.photo_kind === 'after').length ?? 0;
  const invoices = data?.invoices.length ?? 0;
  const urgent = priority === 'urgent_breakdown';
  const photoGateOk = urgent || (before >= 1 && after >= 1);
  const invoiceGateOk = invoices > 0 || e.override_no_invoice === true;

  const gates =
    status === 'in_progress'
      ? [
          { ok: before >= 1, icon: Camera, label: `Before photograph (${before})` },
          { ok: after >= 1, icon: Camera, label: `After photograph (${after})` },
        ]
      : status === 'work_complete'
        ? [{ ok: invoiceGateOk, icon: Receipt, label: invoices > 0 ? `Invoice attached (${invoices})` : 'Invoice attached' }]
        : [];

  const gateBlocked =
    (status === 'in_progress' && !photoGateOk) || (status === 'work_complete' && !invoiceGateOk);

  // The two-man rule, said before the button rather than after the 403. The
  // server remains the authority; this only saves a pointless round trip.
  const same = (col: unknown) => !!user?.id && col != null && String(col) === user.id;
  const conflict =
    status === 'pending_verification' && (same(e.reported_by) || same(e.performed_by))
      ? 'You reported or repaired this job, so it has to be checked by someone else.'
      : status === 'verified' && same(e.verified_by)
        ? 'You checked this job, so approval has to come from someone else.'
        : null;

  const fieldValue = step?.action?.field ? draft.trim() : '';
  const fieldMissing = !!step?.action?.field?.required && fieldValue.length < 2;

  const submit = () => {
    if (!step?.action) return;
    const f = step.action.field;
    move.mutate({
      path: step.action.path,
      body: f ? (fieldValue ? { [f.name]: fieldValue } : {}) : {},
    });
  };

  // Rejection bounces the job back to in_progress, so a job can carry a
  // rejection reason while sitting in an earlier step. Worth showing.
  const sentBack = e.rejected_at && status === 'in_progress' ? String(e.rejection_reason ?? '') : null;

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm text-muted-foreground">{String(e.code ?? '')}</span>
            <span>{e.event_type ? typeLabel(String(e.event_type)) : 'Job'}</span>
            {urgent && (
              <span className="text-[11px] rounded-full border border-rose-500 text-rose-600 dark:text-rose-400 px-2 py-0.5">
                urgent breakdown
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {isLoading && (
          <div className="flex items-center gap-2 text-muted-foreground py-8">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && (
          <div className="rounded-md bg-rose-500/10 border border-rose-500/30 p-3 text-sm text-rose-600 dark:text-rose-400 mt-4">
            This job could not be loaded.
          </div>
        )}

        {data && (
          <div className="space-y-5 mt-4 pb-8">
            {/* The truck, the money, the reading. Three facts, always. */}
            <div className="rounded-lg border border-border px-3 py-2 grid grid-cols-3 gap-2 text-sm">
              <div>
                <div className="text-[11px] text-muted-foreground">Vehicle</div>
                <div className="font-mono" dir="ltr">{String(e.vehicle_plate ?? e.vehicle_code ?? '—')}</div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground">Odometer</div>
                <div className={e.odometer_at_service ? 'font-mono' : 'text-amber-600 dark:text-amber-400'}>
                  {e.odometer_at_service ? `${Number(e.odometer_at_service).toLocaleString('en-US')} km` : 'not recorded'}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-muted-foreground">Total</div>
                <div className="font-mono">{sar(e.total_cost_sar)}</div>
              </div>
            </div>

            {status === 'cancelled' ? (
              <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm">
                <div className="font-medium inline-flex items-center gap-1.5"><Ban className="h-4 w-4" /> This job was cancelled.</div>
                <p className="text-muted-foreground mt-1">It stays on the record, and out of every cost total.</p>
              </div>
            ) : null}

            {sentBack ? (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
                <div className="font-medium inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400">
                  <CornerUpLeft className="h-4 w-4" /> Sent back to the workshop
                </div>
                <p className="text-muted-foreground mt-1">{sentBack}</p>
              </div>
            ) : null}

            {/* Tyre work: two facts and it closes. */}
            {isTyre && status !== 'cancelled' && (
              <div className="rounded-lg border border-border bg-card p-3 space-y-3">
                <div>
                  <div className="text-sm font-medium">Tyre job — recorded, not inspected</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    No diagnosis, no checker, no approval. Two things go on the record and it closes:
                    the reading it was fitted at, and a photograph of the tyre.
                  </p>
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                    <Gauge className="h-3.5 w-3.5" /> Odometer at fitting
                  </label>
                  <div className="flex items-center gap-2">
                    <input inputMode="numeric" value={odo}
                      onChange={(ev) => setOdo(ev.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="e.g. 184500" disabled={alreadyClosed}
                      className="w-40 h-9 rounded-md border border-input bg-background px-3 text-sm font-mono" />
                    <span className="text-xs text-muted-foreground">km</span>
                    {odoOk && <Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />}
                  </div>
                </div>

                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                    <Camera className="h-3.5 w-3.5" /> Photograph of the tyre
                  </label>
                  {photoCount > 0 ? (
                    <div className="text-xs text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5">
                      <Check className="h-3.5 w-3.5" /> {photoCount} on the record
                    </div>
                  ) : (
                    <div className="text-xs text-amber-600 dark:text-amber-400">None yet — one is required.</div>
                  )}
                  {!alreadyClosed && (
                    <input type="file" accept="image/*" capture="environment" disabled={photoBusy}
                      onChange={(ev) => { const f = ev.target.files?.[0]; if (f) void uploadTyrePhoto(f); ev.target.value = ''; }}
                      className="block text-xs mt-1" />
                  )}
                  {photoBusy && <p className="text-xs text-muted-foreground">Uploading…</p>}
                  {photoErr && <p className="text-xs text-rose-600 dark:text-rose-400">{photoErr}</p>}
                </div>

                {alreadyClosed ? (
                  <p className="text-xs text-emerald-600 dark:text-emerald-400 inline-flex items-center gap-1.5">
                    <Check className="h-3.5 w-3.5" /> Closed and recorded.
                  </p>
                ) : (
                  <div className="space-y-1">
                    <Button variant="gold" size="sm"
                      disabled={!odoOk || photoCount < 1 || closeTyre.isPending}
                      onClick={() => closeTyre.mutate()}>
                      {closeTyre.isPending ? 'Closing…' : 'Record it and close'}
                    </Button>
                    {(!odoOk || photoCount < 1) && (
                      <p className="text-[11px] text-muted-foreground">
                        Still needed: {[!odoOk ? 'the odometer reading' : null, photoCount < 1 ? 'a photograph' : null]
                          .filter(Boolean).join(' and ')}.
                      </p>
                    )}
                    {closeTyre.error && (
                      <p className="text-xs text-rose-600 dark:text-rose-400">{extractErrorMessage(closeTyre.error)}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* The ladder — skipped entirely for tyre work. */}
            {!isTyre && (
            <ol className="relative">
              {STEPS.map((s, i) => {
                const done = currentIndex >= 0 && i < currentIndex;
                const here = i === currentIndex;
                const when = s.at ? day(e[s.at]) : null;
                const byName = s.who ? (e[s.who] as string | null) : null;
                const Icon = s.icon;
                return (
                  <li key={s.status} className="flex gap-3 pb-1 last:pb-0">
                    {/* Rail */}
                    <div className="flex flex-col items-center">
                      <div className={
                        'h-7 w-7 rounded-full border flex items-center justify-center shrink-0 ' +
                        (done
                          ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400'
                          : here
                            ? 'border-foreground bg-background text-foreground'
                            : 'border-border bg-background text-muted-foreground/50')
                      }>
                        {done ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                      </div>
                      {i < STEPS.length - 1 && (
                        <div className={'w-px flex-1 min-h-6 ' + (done ? 'bg-emerald-500/40' : 'bg-border')} />
                      )}
                    </div>

                    {/* Body */}
                    <div className={'pb-4 flex-1 ' + (here ? '' : 'opacity-70')}>
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className={'text-sm ' + (here ? 'font-semibold' : done ? '' : 'text-muted-foreground')}>
                          {s.label}
                        </span>
                        {when && <span className="text-[11px] text-muted-foreground font-mono">{when}</span>}
                        {byName && <span className="text-[11px] text-muted-foreground">by {byName}</span>}
                      </div>

                      {/* Only the step you are on carries an action. */}
                      {here && s.action && status !== 'cancelled' && (
                        <div className="mt-2 rounded-lg border border-border bg-card p-3 space-y-3">
                          <p className="text-xs text-muted-foreground">{s.action.hint}</p>

                          {gates.length > 0 && (
                            <ul className="space-y-1">
                              {gates.map((g) => (
                                <li key={g.label} className={
                                  'text-xs inline-flex items-center gap-1.5 w-full ' +
                                  (g.ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400')
                                }>
                                  {g.ok ? <Check className="h-3.5 w-3.5" /> : <g.icon className="h-3.5 w-3.5" />}
                                  {g.label}
                                  {!g.ok && <span className="text-muted-foreground">— still needed</span>}
                                </li>
                              ))}
                              {urgent && status === 'in_progress' && (
                                <li className="text-xs text-muted-foreground">
                                  Urgent breakdown — the photograph rule is waived for this job.
                                </li>
                              )}
                            </ul>
                          )}

                          {s.action.field && (
                            <div className="space-y-1">
                              <label className="text-xs text-muted-foreground">{s.action.field.label}</label>
                              <textarea
                                value={draft}
                                onChange={(ev) => setDraft(ev.target.value)}
                                rows={s.action.field.required ? 3 : 2}
                                placeholder={s.action.field.placeholder}
                                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              />
                            </div>
                          )}

                          {conflict && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 inline-flex items-start gap-1.5">
                              <ShieldCheck className="h-3.5 w-3.5 mt-px shrink-0" /> {conflict}
                            </p>
                          )}

                          <div className="flex flex-wrap gap-2">
                            <Button
                              variant="gold"
                              size="sm"
                              disabled={move.isPending || fieldMissing || gateBlocked || !!conflict}
                              onClick={submit}
                            >
                              {move.isPending ? 'Working…' : s.action.cta}
                            </Button>
                            {(status === 'pending_verification' || status === 'verified') && (
                              <Button variant="outline" size="sm" onClick={() => { setClosing('reject'); setCloseReason(''); }}>
                                Send back
                              </Button>
                            )}
                          </div>

                          {fieldMissing && s.action.field?.required && (
                            <p className="text-[11px] text-muted-foreground">
                              Write at least a couple of words before continuing.
                            </p>
                          )}
                          {move.error && (
                            <p className="text-xs text-rose-600 dark:text-rose-400">{extractErrorMessage(move.error)}</p>
                          )}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
            )}

            {/* Sending back, and cancelling: both need a reason on the record. */}
            {closing && (
              <div className="rounded-lg border border-border p-3 space-y-2">
                <div className="text-sm font-medium">
                  {closing === 'reject' ? 'Send this job back to the workshop' : 'Cancel this job'}
                </div>
                <textarea
                  value={closeReason}
                  onChange={(ev) => setCloseReason(ev.target.value)}
                  rows={2}
                  placeholder={closing === 'reject' ? 'What has to be put right before it can be checked again?' : 'Why is this job being cancelled?'}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="destructive"
                    disabled={move.isPending || closeReason.trim().length < 2}
                    onClick={() => move.mutate(
                      closing === 'reject'
                        ? { path: 'reject', body: { rejectionReason: closeReason.trim() } }
                        : { path: 'cancel', body: { reason: closeReason.trim() } },
                    )}
                  >
                    {move.isPending ? 'Working…' : closing === 'reject' ? 'Send it back' : 'Cancel the job'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setClosing(null)}>Keep it open</Button>
                </div>
                {move.error && (
                  <p className="text-xs text-rose-600 dark:text-rose-400">{extractErrorMessage(move.error)}</p>
                )}
              </div>
            )}

            {status !== 'paid' && status !== 'cancelled' && !closing && (
              <Button variant="ghost" size="sm" className="text-muted-foreground"
                onClick={() => { setClosing('cancel'); setCloseReason(''); }}>
                <Ban className="h-3.5 w-3.5" /> Cancel this job
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
